import React, { useEffect, useRef, useState } from 'react';
import lodash from 'lodash';

import { updateComposerPixelPosition, updateComposerPlayControls, updateProgressPosition } from '../../store/composers/composersSlice';
import { switchSidebar, updateSharedContent, updateSortNameAndDirection } from '../../store/sharedStore/sharedStoreSlice';
import ShareComposerModal from '../SharingModals/SharedWithMePage/ShareComposerModal';
import ShareFolderModal from '../SharingModals/SharedWithMePage/ShareFolderModal';
import { updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import { CONTENTS_SHARED_WITH_ME, VIDEOFEED } from '../../enums/TYPES';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { SharedWithMeFoldersHeader } from './SharedContentHeader';
import { getSharedComposers } from '../../api';
import { ContainedButton } from '../modules/Buttons';
import LinearProgress from '../modules/LinearProgress';
import { getContentsSharedWithMe, getContentsSharedWithMeFoldersComposers } from '../../api/foldersApi';
import { PaginationFooter } from '../modules/PaginationFooter';
import { ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { FilterIcon } from '../../assets/icons/filterIcon';
import { SortOptions, SortType } from '../../enums/SORT';
import { SharedFolder } from '../modules/Folders';
import { OrderSelector } from '../modules/Selector';
import SearchInput from '../modules/SearchInput';
import styles from './SharedWithMe.module.scss';
import SharedComposer from './SharedComposer';
import { FiltersModal } from './Filters';
import {
  buildQueryString,
  deleteMailChecker,
  findSearchValue,
  getMailChecker,
  getSharedWithMePagePaginationLimit,
  getSharedWithMePageSortOptionAndType,
  getSortStateForSharedWithMe,
  parseQueryString,
  setSharedWithMePagePaginationLimit,
  setSharedWithMePageSortOptionAndType,
} from '../../helpers';
import NoMailAccessPage from '../LandingPage/NoMailAccessPage';
import useDebounce from '../../helpers/useDebounce';
import { useContentDragDrop } from '../modules/ContentDragDrop/useContentDragDrop';
import PinnedContentSection from '../modules/PinnedContentSection';

const getSharedContentType = (): 'folder' | 'composer' | 'all' => {
  const wasExplicitlySet = localStorage.getItem('sharedContentTypeTouched') === 'true';
  const storedType = localStorage.getItem('sharedContentType');

  if (wasExplicitlySet && (storedType === 'folder' || storedType === 'composer' || storedType === 'all')) {
    return storedType;
  }

  return 'all';
};

const ACCESS_FILTER_LABELS: Record<string, string> = {
  [ACCESS_TYPE_ENUM.VIEW]: 'View',
  [ACCESS_TYPE_ENUM.VIEW_AND_COMMENT]: 'View & Comment',
  [ACCESS_TYPE_ENUM.COMMENT_AND_CREATE]: 'Comment & Create',
  [ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE]: 'Comment, Create & Reshare',
  [ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE]: 'Edit & Reshare',
  [ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS]: 'Subgroup Specific Access',
  [ACCESS_TYPE_ENUM.NO_ACCESS]: 'No Access',
  [ACCESS_TYPE_ENUM.WITHOUT_ACCESS]: 'No Access',
};

const SharedWithMe = () => {
  const [sort, setSort] = useState<SortType>((getSharedWithMePageSortOptionAndType()?.split('/')[0] as SortType) || SortType.ASC);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState<SortOptions>(
    (getSharedWithMePageSortOptionAndType()?.split('/')[1] as SortOptions) || SortOptions.NAME,
  );
  const [filterBy, setFilterBy] = useState<ACCESS_TYPE_ENUM[] | null>(null);
  const [mailFlag, setMailFlag] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { composerPlayControl } = useAppSelector((state) => state.composersStore);
  const { sharedContent } = useAppSelector((state) => state.sharedStore);
  const searchParams = new URLSearchParams(location.search);
  const noAccess = searchParams.get('noAccess');
  const { currentUser } = useAppSelector((state) => state.user);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const pref = getSharedWithMePagePaginationLimit();
    return pref && lodash.isNumber(+pref) ? +pref : 10;
  });
  const pendingRefetch = useRef(false);

  const [total, setTotal] = useState(0);
  const [updatePage, setUpdatePage] = useState({});
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const [modalType, setModalType] = useState('folder');

  const [type, setType] = useState<'folder' | 'composer' | 'all'>(() => getSharedContentType());

  const activeFilterLabels = [
    ...(type === 'folder' ? ['Folders only'] : []),
    ...(type === 'composer' ? ['Online Composers only'] : []),
    ...((filterBy || []).map((value) => ACCESS_FILTER_LABELS[value] || value)),
  ];
  const hasActiveFilters = activeFilterLabels.length > 0;

  const isFetching = useRef(false);
  const fetchSharedContent = async (sortFromArg?: SortType, sortOpinionFromArg?: SortOptions) => {
    if (isFetching.current) {
      pendingRefetch.current = true;
      return;
    }
    setLoading(true);
    isFetching.current = true;
    try {
      let res: any;
      const activeSort = sortFromArg || sort;
      const activeSortOption = sortOpinionFromArg || sortOption || SortOptions.NAME;
      if (type === 'folder') {
        const prevPageWasSharedComposer = findSearchValue('prevPageWasSharedComposer');
        if (prevPageWasSharedComposer) return;
        res = await getContentsSharedWithMe(page, rowsPerPage, activeSort, search, activeSortOption, filterBy);
        dispatch(updateSortNameAndDirection({ sortOption: activeSortOption, sort: activeSort }));
      } else if (type === 'composer') {
        res = await getSharedComposers({
          page,
          limit: rowsPerPage,
          sort: activeSort,
          search,
          sortOption: activeSortOption,
          accessType: filterBy ? filterBy : undefined,
        });
        dispatch(updateSortNameAndDirection({ sortOption: activeSortOption, sort: activeSort }));
      } else if (type === 'all') {
        res = await getContentsSharedWithMeFoldersComposers(page, rowsPerPage, activeSort, search, activeSortOption, filterBy);
        dispatch(updateSortNameAndDirection({ sortOption: activeSortOption, sort: activeSort }));
      }
      dispatch(updateSharedContent(res.data.documents));
      setTotal(res.data.total);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      isFetching.current = false;

      if (pendingRefetch.current) {
        pendingRefetch.current = false;
        fetchSharedContent();
      }
    }
  };

  useEffect(() => {
    dispatch(switchSidebar(false));
    dispatch(updateNotes([]));
    dispatch(updateQualifiers([]));
    dispatch(updateProgressPosition(0));
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: 0 }));
    dispatch(updateComposerPixelPosition(0));
  }, []);

  const [firstLoad, setFirstLoad] = useState(false);
  useEffect(() => {
    const res = getSharedWithMePageSortOptionAndType();
    if (res && !firstLoad) {
      setFirstLoad(true);
      const sortAndOption = res.split('/');
      setSort(sortAndOption[0] as SortType);
      setSortOption(sortAndOption[1] as SortOptions);
      fetchSharedContent(sortAndOption[0] as SortType, sortAndOption[1] as SortOptions);
    } else {
      fetchSharedContent();
    }
  }, [page, rowsPerPage, debouncedValue, sort, type, updatePage, sortOption, filterBy]);

  const searchContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    e.preventDefault();
    setSearch(e.target.value);
    onPaginate(null, 0);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setSharedWithMePagePaginationLimit(parseInt(e.target.value, 10));
    onPaginate(null, 0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
    if (listRef?.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (loading) return;
    if (option) {
      setSort(option.order);
      setSortOption(option.value as SortOptions);
      setSharedWithMePageSortOptionAndType(option.order, option.value as SortOptions);
    } else if (sortOption === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setSort(sortT);
      setSortOption(singleValue);
      setSharedWithMePageSortOptionAndType(sortT, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForSharedWithMe() ?? 'null');
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      setSort(sortType);
      setSortOption(singleValue);
      setSharedWithMePageSortOptionAndType(sortType, singleValue);
    }
    onPaginate(null, 0);
  };

  const onNameSort = (order?: SortType) => {
    if (loading) return;
    let sortT = sortOption === SortOptions.NAME && sort === SortType.ASC ? SortType.DESC : SortType.ASC;

    if (order) {
      sortT = order;
    }
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.NAME);
    setSortOption(SortOptions.NAME);
    onPaginate(null, 0);
  };

  const onSizeSort = () => {
    if (loading) return;
    let sortT = sortOption === SortOptions.SIZE && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.SIZE);
    setSortOption(SortOptions.SIZE);
    onPaginate(null, 0);
  };

  const onOwnerSort = () => {
    if (loading) return;
    let sortT = sortOption === SortOptions.OWNER && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.OWNER);
    setSortOption(SortOptions.OWNER);
    onPaginate(null, 0);
  };

  const onLastViewedSort = () => {
    if (loading) return;
    const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.LAST_VIEWED);
    setSortOption(SortOptions.LAST_VIEWED);
    onPaginate(null, 0);
  };

  const onLastEditSort = () => {
    if (loading) return;
    const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setSharedWithMePageSortOptionAndType(sortT, SortOptions.UPDATED_AT);
    setSortOption(SortOptions.UPDATED_AT);
    onPaginate(null, 0);
  };

  const sortByPermission = (permissionArr: ACCESS_TYPE_ENUM[] | null) => {
    if (permissionArr && permissionArr.length === 0) {
      setFilterBy(null);
    } else {
      setFilterBy(permissionArr);
    }
    onPaginate(null, 0);
  };

  const [filtersModalOpen, setFiltersModal] = useState(false);

  const switchFiltersModal = () => {
    if (loading) return;
    setFiltersModal(!filtersModalOpen);
  };

  const setTypeFromFilter = (t: any) => {
    if (t === 'folder') {
      const urlObject = parseQueryString(window.location.search);
      if (urlObject?.prevPageWasSharedComposer) {
        delete urlObject?.prevPageWasSharedComposer;
      }
      window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
    }
    localStorage.setItem('sharedContentType', t);
    localStorage.setItem('sharedContentTypeTouched', 'true');
    setType(t);
    onPaginate(null, 0);
  };

  useEffect(() => {
    const storageMailFlag = getMailChecker();

    if (storageMailFlag) {
      const parsedMailFlag = JSON.parse(storageMailFlag);
      setMailFlag(parsedMailFlag);
    }

    const handleBeforeUnload = (event: any) => {
      deleteMailChecker();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const listRef = useRef<HTMLDivElement>(null);
  const orderSelectorOptions = [
    { label: 'A-Z ↓ ', value: SortOptions.NAME, order: SortType.ASC },
    { label: 'Z-A ↑ ', value: SortOptions.NAME, order: SortType.DESC },
    { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
    { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
    { label: 'Last Viewed ↓', value: SortOptions.LAST_VIEWED, order: SortType.DESC },
    { label: 'Last Viewed ↑', value: SortOptions.LAST_VIEWED, order: SortType.ASC },
    { label: 'Last Modified ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
    { label: 'Last Modified ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
    { label: 'Created At ↓', value: SortOptions.CREATED_AT, order: SortType.DESC },
    { label: 'Created At ↑', value: SortOptions.CREATED_AT, order: SortType.ASC },
    { label: 'Owner ↓', value: SortOptions.OWNER, order: SortType.DESC },
    { label: 'Owner ↑', value: SortOptions.OWNER, order: SortType.ASC },
  ].filter((option) => !(type === 'composer' && option.value === SortOptions.SIZE));

  const sharedEntries = (sharedContent as any[]).map((value, feedIndex) => ({ value, feedIndex }));
  const pinnedSharedEntries = page === 0
    ? sharedEntries.filter(({ value }) => value?.is_pinned)
    : [];
  const regularSharedEntries = page === 0
    ? sharedEntries.filter(({ value }) => !value?.is_pinned)
    : sharedEntries;

  const drag = useContentDragDrop({
    items: sharedContent as any[],
    contextType: 'shared_with_me',
    contextParentId: null,
    firstPage: page === 0,
    targetType: (entry) => entry?.type === 'folder' || (!entry?.type && 'name' in entry) ? 'folder' : 'videofeed',
    refresh: () => { if (page !== 0) setPage(0); else fetchSharedContent(); },
    disabled: loading,
  });

  const renderSharedRow = ({ value, feedIndex }: { value: any; feedIndex: number }) => (
    <div
      className="mb-5"
      key={`${
        value?.type === 'composer' || value?.type === 'MY_COMPOSER'
          ? 'composer'
          : 'folder'
      }-${value.id}`}
      {...drag.rowProps(value)}
    >
      {drag.indicator(value)}
      {(value?.type === 'folder' || (!value?.type && 'name' in value)) && (
        <SharedFolder
          setModalType={setModalType}
          data={value as CONTENTS_SHARED_WITH_ME}
          page={page}
          limit={rowsPerPage}
          sort={sort}
          search={search}
          fetchType={type}
          dateSortOption={sortOption}
          pinContextType="shared_with_me"
          pinContextParentId={null}
          onPinUpdated={fetchSharedContent}
        />
      )}
      {(value?.type === 'composer' || value?.type === 'MY_COMPOSER') && (
        <SharedComposer
          setModalType={setModalType}
          feedIndex={feedIndex}
          composer={value as VIDEOFEED}
          owner={false}
          sharedComposer
          dateSortOption={sortOption}
          pinContextType="shared_with_me"
          pinContextParentId={null}
          onPinUpdated={fetchSharedContent}
        />
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>File Management</div>
        <div className={styles.pageTitle}> Shared With Me </div>
      </div>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <SearchInput quickFilter onChange={searchContent} className={styles.searchBar} />
          <div className={styles.sortToolbar}>
            <OrderSelector
              title={'Sorting by'}
              selectedValue={sortOption}
              selectedOrder={sort}
              options={orderSelectorOptions}
              onSelect={sortHandler}
            />
          </div>
        </div>
        <ContainedButton onClick={switchFiltersModal} className={styles.filterButton}>
          <FilterIcon /> Filters
          {hasActiveFilters && <span className={styles.filterBadge}>{activeFilterLabels.length}</span>}
        </ContainedButton>
      </div>
      {hasActiveFilters && (
        <div className={styles.activeFiltersBar}>
          <div className={styles.activeFiltersLabel}>Active filters</div>
          <div className={styles.activeFiltersList}>
            {activeFilterLabels.map((label) => (
              <div key={label} className={styles.activeFilterChip}>
                {label}
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className={styles.foldersListHeader}>
          <SharedWithMeFoldersHeader
            onNameClick={() => onNameSort()}
            onDateSort={sortHandler}
            sort={sort}
            sortOption={sortOption}
            onSizeClick={onSizeSort}
            onOwnerSort={onOwnerSort}
            disableSortBySize={type === 'composer'}
          />
        </div>
        {noAccess && mailFlag ? (
          <NoMailAccessPage />
        ) : (
          <>
            <div ref={listRef} className={styles.list}>
              {(loading || drag.busy) && <LinearProgress />}
              {(pinnedSharedEntries.length > 0 || drag.active) && (
                <div key="pinned-content" {...drag.zoneProps(true)}>
                  <PinnedContentSection count={pinnedSharedEntries.length}>
                    {pinnedSharedEntries.map(renderSharedRow)}
                    {drag.emptyHint(true, pinnedSharedEntries.length === 0)}
                  </PinnedContentSection>
                </div>
              )}
              <div key="regular-content" {...drag.zoneProps(false)}>
                {regularSharedEntries.map(renderSharedRow)}
                {drag.emptyHint(false, regularSharedEntries.length === 0)}
              </div>

              {sharedContent.length === 0 && !loading && (
                <div className={styles.noSharedContentContainer}>
                  <div className={styles.noSharedContentText}>No Shared Content</div>
                  <div className="emptyStateHint">
                    When other users share content with you, it will appear here - check again soon!
                  </div>
                </div>
              )}
            </div>
            <PaginationFooter
              hideVersion
              page={page}
              total={total}
              onPageChange={onPaginate}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsChange}
              pinnedCount={(sharedContent as any[]).filter((item: any) => item?.is_pinned).length}
            />
          </>
        )}
      </div>
      {modalType === 'folder' ? (
        <ShareFolderModal onUpdate={setUpdatePage} refetchFeeds={fetchSharedContent} />
      ) : (
        <ShareComposerModal onUpdate={setUpdatePage} refetchFeeds={fetchSharedContent} />
      )}
      <FiltersModal
        onNameSort={onNameSort}
        onLastViewedSort={onLastViewedSort}
        onLastEditSort={onLastEditSort}
        onSizeSort={onSizeSort}
        sortByPermission={sortByPermission}
        setType={setTypeFromFilter}
        open={filtersModalOpen}
        switchModal={switchFiltersModal}
        sortOption={sortOption}
        sortOrder={sort}
        loading={loading}
        type={type}
        disableSortBySize={type === 'composer'}
      />
    </div>
  );
};

export default SharedWithMe;
